/**
 * GraphicsRenderer — creates PixiJS Graphics shapes from graphics-renderer component data.
 *
 * Supports shape types: rectangle, rounded-rectangle, circle, ellipse,
 * polygon, star, and line. Uses PixiJS 8 draw-then-fill/stroke API.
 */
import { Container, Graphics } from 'pixi.js';
import type { GameObject } from '@core/types/gameobject.types';

export class GraphicsRenderer {
  /** Create a PixiJS container with a Graphics child. */
  static create(gameObject: GameObject): Container {
    const container = new Container();
    const data = gameObject.components.find(c => c.type === 'graphics-renderer')
      ?.data as Record<string, unknown> | undefined;
    if (!data) return container;

    const g = GraphicsRenderer.drawShape(data);
    container.addChild(g);
    return container;
  }

  /** Update an existing container by recreating the Graphics child. */
  static update(container: Container, gameObject: GameObject): void {
    container.removeChildren();
    const data = gameObject.components.find(c => c.type === 'graphics-renderer')
      ?.data as Record<string, unknown> | undefined;
    if (!data) return;

    const g = GraphicsRenderer.drawShape(data);
    container.addChild(g);
  }

  /** Build a Graphics object from component data properties. */
  private static drawShape(data: Record<string, unknown>): Graphics {
    const g = new Graphics();

    const width = (data.width as number) ?? 100;
    const height = (data.height as number) ?? 100;
    const fillColor = (data.fillColor as string) ?? '#ffffff';
    const fillAlpha = (data.fillAlpha as number) ?? 1;
    const strokeColor = (data.strokeColor as string) ?? '#000000';
    const strokeWidth = (data.strokeWidth as number) ?? 0;
    const strokeAlpha = (data.strokeAlpha as number) ?? 1;
    const shape = (data.shape as string) ?? 'rectangle';

    const fillNum = parseInt(fillColor.replace('#', ''), 16);
    const strokeNum = parseInt(strokeColor.replace('#', ''), 16);

    // Draw shape centered on origin
    switch (shape) {
      case 'rectangle':
        g.rect(-width / 2, -height / 2, width, height);
        break;

      case 'rounded-rectangle': {
        const cornerRadius = (data.cornerRadius as number) ?? 8;
        g.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
        break;
      }

      case 'circle': {
        const r = Math.min(width, height) / 2;
        g.circle(0, 0, r);
        break;
      }

      case 'ellipse':
        g.ellipse(0, 0, width / 2, height / 2);
        break;

      case 'polygon': {
        const points = (data.points as number[]) ?? [];
        if (points.length >= 4) {
          g.poly(points);
        }
        break;
      }

      case 'star': {
        const starPoints = (data.points as number) ?? 5;
        const outerR = Math.min(width, height) / 2;
        const innerR = outerR * ((data.innerRadius as number) ?? 0.5);
        g.star(0, 0, starPoints, outerR, innerR);
        break;
      }

      case 'line':
        g.moveTo(-width / 2, 0);
        g.lineTo(width / 2, 0);
        break;
    }

    // Apply fill (not for lines)
    if (shape !== 'line') {
      g.fill({ color: fillNum, alpha: fillAlpha });
    }

    // Apply stroke if width > 0
    if (strokeWidth > 0) {
      g.stroke({ color: strokeNum, alpha: strokeAlpha, width: strokeWidth });
    }

    return g;
  }
}
